import { type FC, useState } from "react";

import { css } from "@emotion/css";
import { Colors, FontSizeInPx, FontWeight, Spacing } from "@snapchat/snap-design-system";
import { StarFilledIcon, StarOutlineIcon } from "@snapchat/snap-design-system-icons";

import { Flexbox } from "./Flexbox.styles";

const starRatingLabel = css`
    margin-bottom: ${Spacing.XS}px;
    font-size: ${FontSizeInPx.COMPACT}px;
    font-weight: ${FontWeight.MEDIUM};
    color: ${Colors.Black.V50};
`;

const starWrapper = css`
    cursor: pointer;
    background: none;
    border: none;
`;

interface StarRating {
    label?: string;
    numberOfStars?: number;
    onClick?: (index: number) => void;
    onMouseEnter?: (index: number) => void;
    onMouseLeave?: () => void;
}

export const StarRating: FC<StarRating> = ({ label, onClick, onMouseEnter, onMouseLeave, numberOfStars = 5 }) => {
    const [selectedClickIndex, setSelectedClickIndex] = useState(0);
    const [selectedHoverIndex, setSelectedHoverIndex] = useState(0);

    const stars = [];

    const handleStarMouseEnter = (index: number) => {
        if (onMouseEnter) {
            onMouseEnter(index);
        }
        setSelectedHoverIndex(index);
    };

    const handleStarMouseLeave = () => {
        if (onMouseLeave) {
            onMouseLeave();
        }
        setSelectedHoverIndex(0);
    };

    const handleStarClick = (index: number) => {
        const isSameIndex = selectedClickIndex === index;
        const newIndex = isSameIndex ? 0 : index;
        if (onClick) {
            onClick(newIndex);
        }
        setSelectedClickIndex(newIndex);
    };

    const upperBoundIndex = Math.max(selectedClickIndex, selectedHoverIndex);

    for (let i = 1; i <= numberOfStars; i++) {
        const Star = i <= upperBoundIndex ? StarFilledIcon : StarOutlineIcon;

        stars.push(
            <button
                key={`star-${i}`}
                onClick={() => {
                    handleStarClick(i);
                }}
                onMouseEnter={() => {
                    handleStarMouseEnter(i);
                }}
                onMouseLeave={handleStarMouseLeave}
                className={starWrapper}
            >
                <Star fill={Colors.Yellow.V200} size={24} />
            </button>,
        );
    }

    return (
        <div>
            {label && <div className={starRatingLabel}>{label}</div>}
            <Flexbox>{stars}</Flexbox>
        </div>
    );
};
